/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';

import classNames from 'classnames';
import { backgroundClassMap } from './constants';
import {
  ComponentCtaButton,
  ComponentCtaSectionBlock,
  EnumCtaSectionBlockBackgroundTheme,
  EnumCtaSectionBlockTextAlign,
} from '$/(cms)/_modules/cmsService/types';
import { CallToActionButton } from '$/components/cms/components-internal/callToActionButton/CallToActionButton';
import ArrowRightAlt from '$/components/icons/ArrowRightAlt';
import { PageContentContainer } from '$/components/PageContentContainer';

export const CtaSectionBlock = ({
  backgroundTheme = EnumCtaSectionBlockBackgroundTheme.PrimaryLight,
  textAlign = EnumCtaSectionBlockTextAlign.Left,
  ctaPrimary,
  ctaSecondary,
  headline,
  subtext,
}: ComponentCtaSectionBlock) => {
  const isCentered = textAlign === EnumCtaSectionBlockTextAlign.Center;
  const isDarkBackground =
    backgroundTheme === EnumCtaSectionBlockBackgroundTheme.Alternative_3;

  const ctas = [ctaPrimary, ctaSecondary].filter(
    (cta): cta is ComponentCtaButton =>
      Boolean(cta?.label && cta?.link && cta?.visible !== false),
  );

  if (!headline && !subtext && !ctas.length) {
    return null;
  }

  return (
    <section
      className={classNames(
        'w-full',
        backgroundClassMap[backgroundTheme],
        isDarkBackground ? 'text-text/on-dark' : 'text-text/primary',
      )}
    >
      <PageContentContainer className="py-spacing/padding/section-y">
        <div
          className={classNames(
            'flex w-full max-w-general/narrow-content-max-width-3 flex-col gap-spacing/gap/y',
            {
              'items-center text-center': isCentered,
              'items-start text-left': !isCentered,
              'mx-auto': isCentered,
            },
          )}
        >
          {(headline || subtext) && (
            <div className="flex w-full flex-col gap-spacing/gap/soft-8px lg:max-w-general/narrow-content-max-width-1">
              {headline && (
                <h2
                  className={classNames(
                    'mhg-alpha-typography-display-1',
                    isDarkBackground
                      ? 'text-text/on-dark'
                      : 'text-text/primary',
                  )}
                >
                  {headline}
                </h2>
              )}
              {subtext && (
                <p
                  className={classNames(
                    'mhg-alpha-typography-body-1-relaxed',
                    isDarkBackground
                      ? 'text-text/on-dark'
                      : 'text-text/secondary',
                  )}
                >
                  {subtext}
                </p>
              )}
            </div>
          )}

          {ctas.length > 0 && (
            <div
              className={classNames(
                'flex w-full flex-col gap-spacing/gap/y md:flex-row md:flex-wrap md:gap-spacing/gap/x',
                {
                  'md:justify-center md:text-center': isCentered,
                  'md:items-start': !isCentered,
                },
              )}
            >
              {ctas.map((cta, index) => (
                <CallToActionButton
                  key={cta.id ?? `${cta.label}-${index}`}
                  cta={cta}
                  className="w-full md:w-auto"
                  iconRight={
                    <ArrowRightAlt className="ml-1 size-shapes/icon-size-lg" />
                  }
                />
              ))}
            </div>
          )}
        </div>
      </PageContentContainer>
    </section>
  );
};
